import { Component, OnDestroy, ViewChild } from '@angular/core';
import { DesignViewComponent } from '@app/design-view/design-view.component';
import {
    BrandLibraryElementDataNode,
    BrandLibraryElementService
} from '@app/design-view/media-library';
import { AssetPropertyContext } from '@app/design-view/properties-panel';
import { CopyPasteService, EditorStateService, HistoryService } from '@app/design-view/services';
import { ElementCreatorService } from '@app/design-view/services/element-creator.service';
import { AnimationService, ElementArrange } from '@app/design-view/timeline';
import { CreativesetDataService } from '@app/shared/creativeset';
import { FontFamiliesService } from '@app/shared/font-families/state/font-families.service';
import { MediaLibraryService } from '@app/shared/media-library/state/media-library.service';
import { getHotkeysAsKeyValueList } from '@app/shared/services/hotkeys/hotkeys';
import { VersionsService } from '@app/shared/versions/state/versions.service';
import { isVersionedProperty } from '@app/shared/versions/versions.utils';
import { UIDropdownComponent, UIDropdownTargetDirective, UINotificationService } from '@bannerflow/ui';
import { hasAnimationsOfType } from '@creative/animation.utils';
import {
    cloneNodeStyles,
    isVersionedText,
    updateStyleIds
} from '@creative/elements/rich-text/text-nodes';
import {
    CreativeDataNode,
    ElementSelection,
    GroupDataNode,
    isImageNode,
    isTextNode,
    isVideoNode
} from '@creative/nodes';
import { canCreateGroup, canMoveSelectionIntoGroup } from '@creative/nodes/data-node.utils';
import {
    isGroupDataNode,
    isHidden,
    isWidgetElement,
    isWidgetNode,
    toFlatElementNodeList,
    toFlatNodeList
} from '@creative/nodes/helpers';
import {
    IFontFamily,
    IGroupElementDataNode,
    INodeWithChildren,
    NodeKind,
    OneOfDataNodes,
    OneOfElementDataNodes
} from '@studio/domain';
import { IElementProperty } from '@studio/domain/creativeset';
import { IBrandLibraryElement } from '@studio/domain/creativeset/brand-library';
import { IVersionProperty } from '@studio/domain/creativeset/version';
import { cloneDeep, distinctArrayById } from '@studio/utils';
import { createElementProperty } from '@studio/utils/element.utils';
import { clamp, omit, toRadians } from '@studio/utils/utils';
import { BehaviorSubject, Subject, merge, takeUntil } from 'rxjs';
import { ElementAlign } from '../../../properties-panel/element-align.enum';
import { ElementDistribution } from '../../../properties-panel/element-distribution.enum';
import { ElementSelectionService } from '../../../services';
import { EditorEventService, ElementChangeType } from '../../../services/editor-event';
import { MutatorService } from '../../../services/mutator.service';
import { StudioWorkspaceComponent } from '../../studio-workspace.component';
import { ContextMenuComponent } from '../context-menu.component';

@Component({
    selector: 'element-menu',
    templateUrl: './element-menu.component.html',
    styleUrls: ['../context-menu.component.scss']
})
export class ElementMenuComponent implements OnDestroy {
    @ViewChild('menu') menu: UIDropdownComponent;
    @ViewChild('menuTrigger') menuTrigger: UIDropdownTargetDirective;

    keyboardShortcuts = getHotkeysAsKeyValueList(['Editor', 'Workspace', 'Timeline']);
    private transformHintDefault = 'Transform';
    transformHint = this.transformHintDefault;
    ElementAlign = ElementAlign;
    AssetPropertyContext = AssetPropertyContext;
    ElementDistribution = ElementDistribution;
    ElementArrange = ElementArrange;
    showUpdateWidgetFromLibrary$ = new BehaviorSubject<boolean>(false);
    selectionHasHiddenElements: boolean;
    selectionHasLockedElements: boolean;
    selectionCanBeReplaced: boolean;
    selectionElementsType: NodeKind.Image | NodeKind.Video;
    isSelectionLocked: boolean;
    selectionIsGroup: boolean;
    selectionIsWidget: boolean;
    canCreateGroup: boolean;
    creativeGroups: { node: IGroupElementDataNode; disabled: boolean }[] = [];
    isBrandLibraryElement$ = new BehaviorSubject<boolean>(false);
    isOneSelected$ = new BehaviorSubject<boolean>(false);
    isBrandLibraryCompatible$ = new BehaviorSubject<boolean>(false);
    showAddToBrandLibrary$ = new BehaviorSubject<boolean>(false);
    isAllowedToMutateBrandLibrary$ = new BehaviorSubject<boolean>(false);
    private ngUnsubscribe$ = new Subject<void>();
    private creativesetFontFamilies: IFontFamily[] = [];
    isDetachable: boolean;

    constructor(
        public workspace: StudioWorkspaceComponent,
        public designView: DesignViewComponent,
        public contextMenu: ContextMenuComponent,
        private history: HistoryService,
        private editorState: EditorStateService,
        private copyPasteService: CopyPasteService,
        private creativesetData: CreativesetDataService,
        private animationService: AnimationService,
        private notification: UINotificationService,
        private mutator: MutatorService,
        private selectionService: ElementSelectionService,
        private editorEventService: EditorEventService,
        private elementCreatorService: ElementCreatorService,
        private brandLibraryElementService: BrandLibraryElementService,
        private mediaLibraryService: MediaLibraryService,
        private fontService: FontFamiliesService,
        private versionsService: VersionsService
    ) {
        this.fontService.creativeSetFontFamilies$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(fontFamilies => (this.creativesetFontFamilies = fontFamilies));

        merge(this.selectionService.change$, this.editorEventService.creative.change$)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.updateChecks();
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.unsubscribe();
    }

    private updateChecks(): void {
        this.checkIfSelectionHasHiddenElements();
        this.checkIfSelectionLockedStatus();
        this.checkIfSelectionIsGroup();
        this.checkIfSelectionIsWidget();
        this.parseGroupsInCreative();
        this.isOneSelected();
        this.isBrandLibraryCompatible();
        this.isBrandLibraryElement();
        this.widgetCanBeUpdatedFromBrandLibrary();
        this.isAllowedToMutateBrandLibrary();
        this.isSelectionDetachable();
        this.showAddToBrandLibrary$.next(
            this.isBrandLibraryCompatible$.getValue() &&
                !this.isBrandLibraryElement$.getValue() &&
                !isWidgetNode(this.selectionService.currentSelection.element)
        );
        this.checkReplaceAsset();
    }

    private isBrandLibraryElement(): void {
        const element = this.selectionService.currentSelection.element;
        const brandLibrary = this.creativesetData.brandLibrary;

        if (element && brandLibrary) {
            const isLibraryElement = [...brandLibrary.elements].some(
                brandLibraryElement => brandLibraryElement.id === element.parentId
            );
            const requirements = this.isOneSelected$.getValue() && isLibraryElement;

            this.isBrandLibraryElement$.next(requirements);

            return;
        }

        this.isBrandLibraryElement$.next(false);
    }

    private isOneSelected(): void {
        const selection = this.selectionService.currentSelection;
        const oneIsSelected = Boolean(selection && selection.length === 1);
        this.isOneSelected$.next(oneIsSelected);
    }

    private isBrandLibraryCompatible(): void {
        const element = this.selectionService.currentSelection.element;
        const brandLibrary = this.creativesetData.brandLibrary;

        if (element && brandLibrary) {
            const blElement = brandLibrary.elements.find(el => el.id === element.parentId);

            if (!blElement && isWidgetNode(element)) {
                this.isBrandLibraryCompatible$.next(false);
                return;
            }

            const requirements = this.isOneSelected$.getValue() && !element.feed;

            this.isBrandLibraryCompatible$.next(requirements);
            return;
        }

        this.isBrandLibraryCompatible$.next(false);
    }

    private isSelectionDetachable(): void {
        const selection = this.selectionService.currentSelection;
        if (!selection?.element) {
            return;
        }

        const selectedElements = selection.elements;

        for (const element of selectedElements) {
            if (!this.isDetachableNode(element)) {
                this.isDetachable = false;
                return;
            }
        }

        this.isDetachable = true;
    }

    private isDetachableNode(node: OneOfDataNodes): boolean {
        const isDetachable =
            isTextNode(node) || isWidgetNode(node) || isImageNode(node) || isVideoNode(node);

        if (!isDetachable) {
            return false;
        }

        const designs = distinctArrayById([
            this.editorState.designFork,
            ...this.creativesetData.creativeset.designs
        ]);

        let nodeInstances = 0;
        for (const design of designs) {
            const elementExist = design.elements.some(({ id }) => id === node.id);
            if (elementExist) {
                nodeInstances++;
            }
        }

        return nodeInstances > 1;
    }

    selectionLength(): boolean | undefined {
        if (this.designView.copiedSnapshot) {
            return (
                this.designView.copiedSnapshot.selection &&
                this.designView.copiedSnapshot.selection.length === 1
            );
        }
        return false;
    }

    canPasteLayout(): boolean {
        const copiedSelection = this.designView.copiedSnapshot;
        if (copiedSelection) {
            return copiedSelection.selection ? true : false;
        }
        return false;
    }

    canPasteAnimations(): boolean {
        const copiedSelection = this.designView.copiedSnapshot;
        if (copiedSelection) {
            return copiedSelection.selection ? true : false;
        }
        return false;
    }

    deleteElement(): void {
        this.workspace.removeSelectedElements();
    }

    async duplicate(): Promise<void> {
        const snapshot = this.history.createSnapshot();
        await this.designView.pasteSelection(snapshot, undefined, true);
    }

    async detach(): Promise<void> {
        const selection = this.selectionService.currentSelection;
        const nodes = selection.elements.filter(element => this.isDetachableNode(element));

        if (!nodes.length) {
            return;
        }

        const selectedGroups = selection.nodes.filter(isGroupDataNode);

        this.selectionService.clearSelection();

        const creativeDataNode = this.editorState.document;
        creativeDataNode.preserveEmptyChildren = true;

        const newNodes: OneOfElementDataNodes[] = [];
        for (const node of nodes) {
            const newNode = await this.detachElement(node);
            if (newNode) {
                newNodes.push(newNode);
            }
        }

        creativeDataNode.preserveEmptyChildren = false;

        if (!newNodes.length) {
            return;
        }

        if (selectedGroups.length) {
            this.editorEventService.creative.change('nodes', selectedGroups);
            this.selectionService.setSelection(...selectedGroups);
        } else {
            this.editorEventService.creative.change('elements', newNodes);
            this.selectionService.setSelection(...newNodes);
        }

        this.notification.open(
            `Selected element ${nodes.length >= 2 ? 's' : ''} has been detached from other sizes`,
            {
                autoCloseDelay: 3000,
                placement: 'top',
                type: 'info'
            }
        );
    }

    private async detachElement(
        node: OneOfElementDataNodes
    ): Promise<OneOfElementDataNodes | undefined> {
        const elementProperties = this.detachElementProperties(node);
        const parent = node.__parentNode || node.__rootNode;

        if (!parent) {
            throw new Error('Not possible to detach an node without a parent.');
        }

        const currentIndex = toFlatNodeList(parent).findIndex(({ id }) => id === node.id)!;

        const elementWasRemoved = await this.workspace.removeElement(
            new ElementSelection([node]),
            false
        );

        if (!elementWasRemoved) {
            return;
        }

        const { defaultVersionProperties, versionProperties } = this.editorState;

        const newElement = this.elementCreatorService.createElementCopy(
            node,
            {
                values: {
                    defaultVersionProperties,
                    elementProperties,
                    versionProperties
                },
                shouldRescale: false
            },
            true
        );

        parent.moveNode_m!(newElement, currentIndex);

        return newElement;
    }

    private detachElementProperties(node: OneOfElementDataNodes): IElementProperty[] {
        const { versionProperties, elements, defaultVersionProperties } = this.editorState;
        const allVersionProperties = distinctArrayById([
            ...versionProperties,
            ...defaultVersionProperties
        ]);
        const element = cloneDeep(elements.find(({ id }) => id === node.id));

        if (!element) {
            return [];
        }

        const elementProperties = element.properties.map(property =>
            createElementProperty(omit(property, 'id', 'clientId'))
        );

        const styleIdMap = isTextNode(node)
            ? cloneNodeStyles(node, this.editorState.document.id)
            : undefined;

        for (const elementProperty of elementProperties) {
            const versionProperty = allVersionProperties.find(
                ({ id }) => id === elementProperty.versionPropertyId
            );

            if (!versionProperty) {
                continue;
            }

            const oldVersionProperty = cloneDeep(versionProperty);

            if (elementProperty.versionPropertyId) {
                elementProperty.value = oldVersionProperty.value;

                this.updateAndUpsertVersionProperties(
                    node,
                    oldVersionProperty,
                    elementProperty,
                    styleIdMap
                );
            }
        }

        return elementProperties;
    }

    private updateAndUpsertVersionProperties(
        node: OneOfElementDataNodes,
        oldVersionProperty: IVersionProperty,
        elementProperty: IElementProperty,
        styleIdMap?: Map<string, string>
    ): void {
        if (!isVersionedProperty(oldVersionProperty)) {
            return;
        }

        const currentDocumentId = this.editorState.document.id;
        const unitName = oldVersionProperty.name;
        const versionedElementProperty = this.editorState.propertyAsVersionableProperty(
            elementProperty,
            unitName
        );
        const currentVersion = this.editorState.currentVersion;
        const versions = distinctArrayById([currentVersion, ...this.editorState.versions]);

        if (isWidgetNode(node)) {
            const customProperty = node.customProperties.find(
                ({ versionPropertyId }) => versionPropertyId === oldVersionProperty.id
            );
            if (customProperty) {
                customProperty.versionPropertyId = versionedElementProperty.versionPropertyId;
            }
        }

        for (const version of versions) {
            const property = version.properties.find(({ id }) => id === oldVersionProperty.id);

            if (!property) {
                continue;
            }

            if (isVersionedText(property)) {
                // Clean up documentid references that no longer are valid
                const oldProperty = cloneDeep(property);

                for (const style of oldProperty.value.styles) {
                    delete style.styleIds[currentDocumentId];
                }

                this.versionsService.upsertVersionProperty(
                    version.id,
                    cloneDeep({
                        ...oldProperty,
                        id: oldProperty.id
                    })
                );
            }

            const newProperty = cloneDeep(property);

            if (isVersionedText(newProperty) && styleIdMap) {
                updateStyleIds(newProperty, styleIdMap);
            }

            const newVersionProperty: IVersionProperty = {
                ...newProperty,
                id: versionedElementProperty.versionPropertyId
            };

            this.versionsService.upsertVersionProperty(version.id, newVersionProperty);
        }
    }

    toggleVisibility(): void {
        const selection = this.selectionService.currentSelection;

        if (!selection.elements.length) {
            return;
        }

        if (selection.length) {
            this.mutator.toggleElementsVisibility(
                toFlatNodeList(selection.nodes),
                this.selectionHasHiddenElements
            );
        }

        this.checkIfSelectionHasHiddenElements();
    }

    fillElementsToCanvas(): void {
        const selection = this.selectionService.currentSelection;
        if (!selection) {
            throw new Error('No selection found when filling elements to canvas');
        }
        this.mutator.fillToCanvas(selection);
    }

    group = (): void => {
        const selection = this.selectionService.currentSelection;

        const sortedNodes = selection.nodesAsSortedArray().slice().reverse();
        this.elementCreatorService.createGroup(sortedNodes);
    };

    ungroup = (): void => {
        const selection = this.selectionService.currentSelection;
        const groups = selection.nodes.filter(node => isGroupDataNode(node));
        this.mutator.removeNodeGroups(groups as GroupDataNode[]);

        // Re-parse groups in case a group is destroyed due to being last node
        this.parseGroupsInCreative();
    };

    moveToGroup(group: IGroupElementDataNode): void {
        const nodes = this.selectionService.currentSelection.nodes;
        if (!nodes.length) {
            return;
        }

        this.mutator.moveNodesToGroup(group, nodes);
    }

    toggleLock = (): void => {
        const selection = this.selectionService.currentSelection;
        if (!selection) {
            throw new Error('No selection found when toggling lock');
        }

        this.mutator.setLocked(selection.nodes, !this.isSelectionLocked);
        this.workspace.deselectAllElements();
    };

    cut(): void {
        this.designView.cutSelection();
    }

    copy(): void {
        const snapshot = (this.copyPasteService.copiedSnapshot = this.history.createSnapshot());
        this.copyPasteService.copyAndSetLocalstorage(
            snapshot,
            this.creativesetData.brand.id,
            this.creativesetFontFamilies
        );
    }

    paste(): void {
        void this.designView.onPaste();
    }

    pasteStyle(): void {
        this.designView.onPasteStyle();
    }

    pasteLayout(): void {
        this.designView.onPasteLayout();
    }

    pasteAnimations(): void {
        this.designView.onPasteAnimations();
    }

    canMoveBackward(): boolean {
        const selectedNodes = this.selectionService.currentSelection.nodes;
        return selectedNodes
            .filter(node => !!node.__parentNode || !!node.__rootNode)
            .some(node => {
                const context = node.__parentNode ?? node.__rootNode;
                const index = this.getNodeIndexWithinParentContext(node, context!);
                return (
                    index > 0 &&
                    !selectedNodes.find(
                        selectedNode => selectedNode.id === context!.nodes[index - 1].id
                    )
                );
            });
    }

    canMoveForward(): boolean {
        const selectedNodes = this.selectionService.currentSelection.nodes;
        return selectedNodes
            .filter(node => !!node.__parentNode || !!node.__rootNode)
            .some(node => {
                const context = node.__parentNode ?? node.__rootNode;
                const index = this.getNodeIndexWithinParentContext(node, context!);
                return (
                    index < context!.nodes.length - 1 &&
                    !selectedNodes.find(
                        selectedNode => selectedNode.id === context!.nodes[index + 1].id
                    )
                );
            });
    }

    private getNodeIndexWithinParentContext(node: OneOfDataNodes, context: INodeWithChildren): number {
        return context.nodes.findIndex(ctxNode => ctxNode.id === node.id);
    }

    alignSelection(align: ElementAlign): void {
        this.workspace.alignSelection(align);
    }

    distributeSelection(distribution: ElementDistribution): void {
        this.workspace.distributeSelection(distribution);
    }

    isAligned(align: ElementAlign): boolean {
        const selection = this.selectionService.currentSelection;
        if (!selection) {
            throw new Error('No selection found when checking alignment');
        }
        const alignments = this.mutator.getElementsAlignmentWithinSelection(selection);
        return alignments.indexOf(align) >= 0;
    }

    arrange(arrange: ElementArrange): void {
        const selection = this.selectionService.currentSelection.nodesAsSortedArray();
        if (!selection.length) {
            return;
        }

        // reverse the array to keep the right order when updating elements one by one in certain directions
        const isForwardOrBack = arrange === ElementArrange.Forward || arrange === ElementArrange.Back;

        const selectedNodes = isForwardOrBack ? selection.slice().reverse() : selection;
        const movedElements: OneOfDataNodes[] = [];
        for (const node of selectedNodes) {
            const context = node.__parentNode ?? node.__rootNode;
            if (!context) {
                continue;
            }
            let moved = false;
            switch (arrange) {
                case ElementArrange.Forward:
                    moved = this.moveForward(node, context);
                    break;
                case ElementArrange.Backward:
                    moved = this.moveBackward(node, context);
                    break;
                case ElementArrange.Front:
                    moved = this.moveToFront(node, context);
                    break;
                case ElementArrange.Back:
                    moved = this.moveToBack(node, context);
                    break;
            }
            if (moved) {
                movedElements.push(node);
            }
        }
        this.emitCreativeElementChange(movedElements);
    }

    private moveBackward(
        node: OneOfDataNodes,
        context: IGroupElementDataNode | CreativeDataNode
    ): boolean {
        const currentIndexInContext = this.getNodeIndexWithinParentContext(node, context);
        const newIndexInContext = clamp(currentIndexInContext - 1, 0, context.nodes.length - 1);
        const selectedNodes = this.selectionService.currentSelection.nodesAsSortedArray();
        const isNextWithinSelection = !!selectedNodes.find(
            selectedNode => selectedNode.id === context.nodes[newIndexInContext].id
        );
        if (!isNextWithinSelection && currentIndexInContext !== newIndexInContext) {
            this.moveToIndexWithinCurrentContext(node, newIndexInContext, context);
            return true;
        }
        return false;
    }

    private moveForward(
        node: OneOfDataNodes,
        context: IGroupElementDataNode | CreativeDataNode
    ): boolean {
        const currentIndexInContext = this.getNodeIndexWithinParentContext(node, context);
        const newIndexInContext = clamp(currentIndexInContext + 1, 0, context.nodes.length - 1);
        const isNextWithinSelection = !!this.selectionService.currentSelection.nodes.find(
            selectedNode => selectedNode.id === context.nodes[newIndexInContext].id
        );
        if (!isNextWithinSelection && currentIndexInContext !== newIndexInContext) {
            this.moveToIndexWithinCurrentContext(node, newIndexInContext, context);
            return true;
        }
        return false;
    }

    private moveToFront(
        node: OneOfDataNodes,
        context: IGroupElementDataNode | CreativeDataNode
    ): boolean {
        const currentIndexInContext = this.getNodeIndexWithinParentContext(node, context);
        const newIndexInContext = context.nodes.length - 1;
        if (currentIndexInContext !== newIndexInContext) {
            this.moveToIndexWithinCurrentContext(node, newIndexInContext, context);
            return true;
        }
        return false;
    }

    private moveToBack(
        node: OneOfDataNodes,
        context: IGroupElementDataNode | CreativeDataNode
    ): boolean {
        const currentIndexInContext = this.getNodeIndexWithinParentContext(node, context);
        const newIndexInContext = 0;
        if (currentIndexInContext !== newIndexInContext) {
            this.moveToIndexWithinCurrentContext(node, newIndexInContext, context);
            return true;
        }
        return false;
    }

    private moveToIndexWithinCurrentContext(
        node: OneOfDataNodes,
        newIndex: number,
        context: IGroupElementDataNode | CreativeDataNode
    ): void {
        const currentIndexInContext = this.getNodeIndexWithinParentContext(node, context);
        if (newIndex !== currentIndexInContext) {
            context.removeNodeById_m(node.id);
            context.addNode_m(node, newIndex);
            this.mutator.renderer.updateElementOrder_m();
        }
    }

    private emitCreativeElementChange(elements: OneOfDataNodes[]): void {
        const nodes = toFlatElementNodeList(elements);
        this.editorEventService.creative.change('elements', nodes);
    }

    rotate(degrees: number, eventType?: ElementChangeType): void {
        const selection = this.selectionService.currentSelection;
        if (selection && selection.length > 0) {
            this.setTransformHint(`Rotate ${degrees > 0 ? '+' : ''}${degrees}°`);

            selection.elements.forEach(element => {
                this.mutator.setRotationZ(
                    element,
                    (element.rotationZ ?? 0) + toRadians(degrees),
                    eventType
                );
            });
        }
    }

    revertRotate(degrees: number): void {
        this.rotate(-1 * degrees);
        this.resetTransformHint();
    }

    flip(direction: 'mirrorX' | 'mirrorY', eventType?: ElementChangeType): void {
        const selection = this.selectionService.currentSelection;
        if (selection && selection.length > 0) {
            this.setTransformHint(direction === 'mirrorX' ? 'Flip H' : 'Flip V');

            selection.elements.forEach(element => {
                this.mutator.flip(element, direction, eventType);
            });
        }
    }

    revertFlip(direction: 'mirrorX' | 'mirrorY'): void {
        this.flip(direction);
        this.resetTransformHint();
    }

    setTransformHint(text: string): void {
        this.transformHint = text;
    }

    resetTransformHint(): void {
        this.setTransformHint(this.transformHintDefault);
    }

    applyTransform(transform: number | 'mirrorX' | 'mirrorY'): void {
        if (typeof transform === 'number') {
            // revert preview before applying
            this.revertRotate(transform);
            this.rotate(transform, ElementChangeType.Instant);
        } else {
            this.revertFlip(transform);
            this.flip(transform, ElementChangeType.Instant);
        }
        this.resetTransformHint();
    }

    widgetCanBeUpdatedFromBrandLibrary(): void {
        if (!this.creativesetData.brandLibrary || this.selectionService.currentSelection.length > 1) {
            return;
        }

        const element = this.selectionService.currentSelection.element;
        if (element && isWidgetNode(element)) {
            const widget = [...this.creativesetData.brandLibrary.elements].find(
                w => w.id === element.parentId
            );
            this.showUpdateWidgetFromLibrary$.next(!!widget);
            return;
        }
        this.showUpdateWidgetFromLibrary$.next(false);
    }

    private checkReplaceAsset(): void {
        const isAllSelectionElementsVideos = this.selectionService.currentSelection.elements.every(
            element => isVideoNode(element)
        );
        const isAllSelectionElementsImages = this.selectionService.currentSelection.elements.every(
            element => isImageNode(element)
        );
        this.selectionCanBeReplaced = isAllSelectionElementsVideos || isAllSelectionElementsImages;
        if (isAllSelectionElementsVideos) {
            this.selectionElementsType = NodeKind.Video;
        } else if (isAllSelectionElementsImages) {
            this.selectionElementsType = NodeKind.Image;
        }
    }

    private isAllowedToMutateBrandLibrary(): void {
        this.isAllowedToMutateBrandLibrary$.next(
            this.isBrandLibraryElement$.getValue() &&
                !isWidgetNode(this.selectionService.currentSelection.element)
        );
    }

    async createNewElementInMediaLibrary(): Promise<void> {
        const element = this.selectionService.currentSelection.element as BrandLibraryElementDataNode;
        if (this.isBrandLibraryCompatible$.getValue() && element) {
            await this.brandLibraryElementService.add(
                element,
                this.designView.renderer.creativeDocument
            );
        }
    }

    async updateElementInBrandLibrary(): Promise<void> {
        const element = this.selectionService.currentSelection.element as BrandLibraryElementDataNode;
        if (this.isBrandLibraryCompatible$.getValue() && element) {
            const creativesetElement = this.creativesetData.brandLibrary.elements.find(
                x => x.id === element.parentId
            );

            if (creativesetElement) {
                await this.brandLibraryElementService.update(element, creativesetElement);
            }
        }
    }

    async uploadToMediaLibrary(elementAlreadyExistsInBrandLibrary: boolean): Promise<void> {
        this.contextMenu.tryCloseMenus();
        const element = this.selectionService.currentSelection.element;

        if (element && !elementAlreadyExistsInBrandLibrary) {
            await this.createNewElementInMediaLibrary();
        } else if (element && elementAlreadyExistsInBrandLibrary) {
            await this.updateElementInBrandLibrary();
        }
    }

    updateWidgetFromLibrary(): void {
        this.contextMenu.tryCloseMenus();
        const documentElement = this.selectionService.currentSelection.element;

        if (!isWidgetNode(documentElement)) {
            return;
        }

        const bannerflowWidgets = this.designView.mediaLibrary.bannerflowLibraryWidgets || [];
        const widgets = this.creativesetData.brandLibrary.elements.filter(isWidgetElement);
        const libraryElementId = documentElement.parentId;
        const element = this.editorState.elements.find(({ id }) => id === documentElement.id)!;

        if (!libraryElementId || !element) {
            throw new Error('Could not find element or library when updating widget from library');
        }

        const widgetElement = widgets.find(({ id }) => id === libraryElementId);
        const bannerflowWidget = bannerflowWidgets.find(({ id }) => id === libraryElementId);
        const libraryWidget = (widgetElement || bannerflowWidget) as IBrandLibraryElement;

        if (!libraryWidget) {
            return;
        }

        this.selectionService.clearSelection();
        const zIndex = this.editorState.renderer.getNodeIndex_m(element);
        this.editorState.document.preserveEmptyChildren = true;
        this.mutator.removeSelection(documentElement, true);

        const newWidget = this.brandLibraryElementService.getUpdatedWidgetElementFromLibrary(
            documentElement,
            libraryWidget
        );

        if (!newWidget) {
            throw new Error('Could not get updated widget from library');
        }

        this.editorState.document.preserveEmptyChildren = false;

        this.mutator.setElementIndex(newWidget, zIndex);

        this.emitCreativeElementChange([newWidget]);

        // Do this right away to make sure everything is in sync for the history snapshot
        this.designView.rerenderNode(newWidget);
        this.selectionService.setSelection(newWidget);
        this.workspace.redrawGizmos();
    }

    hasAnimation(): boolean {
        const element = this.selectionService.currentSelection.element;
        if (element && hasAnimationsOfType(element, 'keyframe')) {
            return true;
        }
        return false;
    }

    addAnimation(): void {
        if (!this.hasAnimation()) {
            this.animationService.addAnimation$.next();
        }
    }

    private checkIfSelectionLockedStatus(): void {
        this.selectionHasLockedElements = this.selectionService.currentSelection.elements.some(
            node => node.locked
        );
        this.isSelectionLocked = this.selectionService.currentSelection.nodes.every(
            node => node.locked
        );
    }

    checkIfSelectionHasHiddenElements(checkIfAllHidden?: boolean): void {
        const selection = this.selectionService.currentSelection;

        if (!selection) {
            throw new Error('No selection found when checking if selection has hidden elements');
        }

        const nodes = toFlatNodeList(selection.nodes);

        if (checkIfAllHidden) {
            this.selectionHasHiddenElements = nodes.every(element => isHidden(element));
        } else {
            this.selectionHasHiddenElements = nodes.some(element => isHidden(element));
        }
    }

    private checkIfSelectionIsGroup(): void {
        const selection = this.selectionService.currentSelection;
        if (!selection) {
            throw new Error('No selection found when checking if selection has hidden elements');
        }

        const selectionIsOnlyGroups = selection.nodes.every(node => isGroupDataNode(node));

        if (selectionIsOnlyGroups) {
            this.selectionIsGroup = true;
        } else {
            this.selectionIsGroup = false;
        }

        this.canCreateGroup = canCreateGroup(
            selection.nodes,
            this.editorState.renderer.creativeDocument
        );
    }

    private checkIfSelectionIsWidget(): void {
        this.selectionIsWidget = isWidgetNode(this.selectionService.currentSelection.element);
    }

    private parseGroupsInCreative(): void {
        this.creativeGroups = [];
        const selectedNodes = toFlatNodeList(this.selectionService.currentSelection.nodes);

        for (const node of this.editorState.document.nodeIterator_m(true)) {
            if (isGroupDataNode(node)) {
                const disabled =
                    selectedNodes.some(selectedNode => selectedNode.__parentNode?.id === node.id) ||
                    !canMoveSelectionIntoGroup(selectedNodes, node);
                this.creativeGroups.push({ node, disabled });
            }
        }
    }

    findInBrandLibrary = (): void => {
        const selection = this.selectionService.currentSelection;

        if (!selection.element || selection.elements.length !== 1) {
            return;
        }

        this.mediaLibraryService.filterByElement(selection.element);
    };
}
